import { useAsyncData, useRequestURL } from '#imports'

import {
  getArticleContent,
  getArticleVotes,
} from '@backmarket/http-api/src/api-specs-content/endpoints'
import type {
  ApiArticlePageContent,
  ArticlePageContent,
} from '@backmarket/http-api/src/api-specs-content/models/article-page-content'
import type { MappedApiPageContent } from '@backmarket/http-api/src/api-specs-content/models/base-page-content'
import type { ArticleCard } from '@backmarket/nuxt-layer-cms/ArticleCard/ArticleCard.types'
import { useHttpFetch } from '@backmarket/nuxt-module-http/useHttpFetch'
import { useHttpFetch as useHttpFetchV2 } from '@backmarket/nuxt-module-http-v2/useHttpFetch'

import { useFetchCMSPageContentWithFallback } from '~/scopes/cms/composables/useFetchCMSPageContentWithFallback'
import { useFetchFormattedPageContent } from '~/scopes/cms/composables/useFetchFormattedPageContent'
import { usePageParamsFromRoute } from '~/scopes/cms/composables/usePageParamsFromRoute'
import { useSetCMSPageCacheTagHeader } from '~/scopes/cms/composables/useSetCMSPageCacheTagHeader'

function useFetchVotes() {
  const { pageName } = usePageParamsFromRoute()

  return useHttpFetch(getArticleVotes, {
    pathParams: { articleSlug: pageName },
  })
}

const formatArticle = (
  response: MappedApiPageContent<ApiArticlePageContent>,
): MappedApiPageContent<ArticlePageContent> => {
  const { updateDate, displayBreadcrumb, ...res } = response

  return {
    ...res,
    updateDate: updateDate ? new Date(updateDate) : undefined,
    displayBreadcrumb:
      typeof displayBreadcrumb === 'boolean' ? displayBreadcrumb : true,
  }
}

function useFetchCMSPageContent({
  fetchFallbackOnError,
}: {
  fetchFallbackOnError: boolean
}) {
  const pathParams = usePageParamsFromRoute()

  if (fetchFallbackOnError)
    return useFetchCMSPageContentWithFallback(
      '/content/page/:pageType/:pageName',
      {
        pathParams,
        transform: formatArticle,
      },
    )

  return useFetchFormattedPageContent(getArticleContent, {
    pathParams,
    transform: formatArticle,
  })
}

function useFetchRelatedArticles() {
  const $httpFetch = useHttpFetchV2()
  const requestURL = useRequestURL()

  return useAsyncData(
    () =>
      $httpFetch('/internal-links/v1/cards', {
        query: { source_url: requestURL.pathname },
      }),
    {
      transform: (response): ArticleCard[] => {
        const relatedArticles = response.linkLists.find(
          (l) => l.listId === 'related_articles',
        )
        if (!relatedArticles) return []

        return relatedArticles.links.map((article) => {
          return {
            title: article.title ?? '',
            category: article.tagline ?? '',
            text: article.description ?? '',
            id: article.link.href,
            imageSrc: article.image.src ?? '',
            imageAlt: article.image.alt ?? undefined,
            link: { href: article.link.relativeHref },
          }
        })
      },
      default: () => [],
    },
  )
}

export async function useFetchArticle({
  fetchFallbackOnError,
}: {
  fetchFallbackOnError: boolean
}) {
  const { setCMSCacheTagHeader } = useSetCMSPageCacheTagHeader()
  const [fetchedContent, fetchedVotes, fetchedRelatedArticles] =
    await Promise.all([
      useFetchCMSPageContent({ fetchFallbackOnError }),
      useFetchVotes(),
      useFetchRelatedArticles(),
    ])

  setCMSCacheTagHeader(fetchedContent?.data?.value)

  return {
    fetchedContent,
    fetchedVotes,
    fetchedRelatedArticles,
  }
}
